"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildDynamicTransactions = exports.buildDynamicTransactionsNoSigning = exports.getTransactionSize = exports.MAX_TRANSACTION_SIZE = void 0;
const web3_js_1 = require("@solana/web3.js");
const neverthrow_1 = require("neverthrow");
const util_1 = require("../util");
const transactionHandling_1 = require("./transactionHandling");
exports.MAX_TRANSACTION_SIZE = 1232;
function getTransactionSize(instructions, funder) {
    const uniqueSigners = new Set();
    uniqueSigners.add(funder.toBase58());
    instructions.forEach(({ instruction }) => instruction.keys.forEach((s) => {
        if (s.isSigner) {
            uniqueSigners.add(s.pubkey.toBase58());
        }
    }));
    const uniqueKeys = new Set();
    uniqueKeys.add(funder.toBase58());
    let ixSizes = 0;
    instructions.forEach(({ instruction }) => {
        uniqueKeys.add(instruction.programId.toBase58());
        instruction.keys.forEach((k) => uniqueKeys.add(k.pubkey.toBase58()));
        ixSizes +=
            1 + // program id index
                lengthToCompact16size(instruction.keys.length) + // num accounts
                instruction.keys.length + // account indexes
                lengthToCompact16size(instruction.data.length) + // num ix bytes
                instruction.data.length; // ix bytes
    });
    // console.log({ uniqueSigners, uniqueKeys, ixSizes });
    return (lengthToCompact16size(uniqueSigners.size) + // num sigs
        uniqueSigners.size * 64 + // Sigs
        3 + // message header
        lengthToCompact16size(uniqueKeys.size) + // num accounts
        uniqueKeys.size * 32 + // accounts
        32 + // recent blockhash
        lengthToCompact16size(instructions.length) + // num instructions
        ixSizes);
}
exports.getTransactionSize = getTransactionSize;
const insert = (arr, index, newItem) => [
    // part of the array before the specified index
    ...arr.slice(0, index),
    // inserted item
    newItem,
    // part of the array after the specified index
    ...arr.slice(index),
];
const deepCopyInstructions = (input) => {
    return input.map((entry) => (Object.assign(Object.assign({}, entry), { instruction: new web3_js_1.TransactionInstruction({
            keys: entry.instruction.keys.map((k) => (Object.assign({}, k))),
            programId: entry.instruction.programId,
            data: entry.instruction.data
                ? Buffer.from(entry.instruction.data)
                : undefined,
        }) })));
};
function buildDynamicTransactionsNoSigning(instructions, feePayer, beforeIxs = [], afterIxs = []) {
    return __awaiter(this, void 0, void 0, function* () {
        const instructionsWithSigners = (0, util_1.normalizeArray)(yield Promise.all((0, util_1.normalizeArray)(instructions).map((ix) => ix(feePayer)))).flat();
        const beforeIxsWithSigners = (0, util_1.normalizeArray)(yield Promise.all((0, util_1.normalizeArray)(beforeIxs).map((ix) => ix(feePayer)))).flat();
        const afterIxsWithSigners = (0, util_1.normalizeArray)(yield Promise.all((0, util_1.normalizeArray)(afterIxs).map((ix) => ix(feePayer)))).flat();
        const output = [];
        let current = [
            ...beforeIxsWithSigners,
            ...afterIxsWithSigners,
        ];
        for (const ix of instructionsWithSigners) {
            const next = insert(current, current.length - afterIxsWithSigners.length, ix);
            const nextSize = getTransactionSize(next, feePayer.publicKey());
            if (nextSize > exports.MAX_TRANSACTION_SIZE) {
                output.push(deepCopyInstructions(current));
                current = [...beforeIxsWithSigners, ix, ...afterIxsWithSigners];
                if (getTransactionSize(current, feePayer.publicKey()) > exports.MAX_TRANSACTION_SIZE) {
                    return (0, neverthrow_1.err)(`Instruction too large to fit in transaction: ${ix.instruction.programId.toBase58()}`);
                }
            }
            else {
                current = next;
            }
        }
        if (current.length >
            beforeIxsWithSigners.length + afterIxsWithSigners.length) {
            output.push(deepCopyInstructions(current));
        }
        return (0, neverthrow_1.ok)(output);
    });
}
exports.buildDynamicTransactionsNoSigning = buildDynamicTransactionsNoSigning;
/**
 * Builds dynamic transactions from a set of instructions, fee payer, connection, and optional before/after instructions.
 *
 * @param instructions - The main instructions to include in the transactions.
 * @param feePayer - The fee payer for the transactions.
 * @param connectionOrRbh - The connection information or recent blockhash with expiry and commitment.
 * @param beforeIxs - Optional instructions to include at the beginning of each transaction.
 * @param afterIxs - Optional instructions to include at the end of each transaction.
 * @returns A promise that resolves to a result object containing either the built transactions or an error message.
 */
function buildDynamicTransactions(instructions, feePayer, connectionOrRbh, beforeIxs = [], afterIxs = []) {
    return __awaiter(this, void 0, void 0, function* () {
        const output = yield buildDynamicTransactionsNoSigning(instructions, feePayer, beforeIxs, afterIxs);
        if (output.isErr()) {
            return (0, neverthrow_1.err)(output.error);
        }
        return (0, neverthrow_1.ok)(yield (0, transactionHandling_1.buildAndSignTransactionsFromIxWithSigners)(output.value.map((ixs) => ({ ixs, connectionOrRbh })), feePayer));
    });
}
exports.buildDynamicTransactions = buildDynamicTransactions;
function lengthToCompact16size(size) {
    if (size > 0x7f) {
        return 2;
    }
    else {
        return 1;
    }
}
//# sourceMappingURL=sizing.js.map